import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useGoBack } from '../../hooks/useGoBack'
import { CheckCircle2, Instagram, Link as LinkIcon, Search } from 'lucide-react'
import Header from '../../components/Header'
import { useI18n } from '../../i18n/useI18n'
import { allPrograms, useStampRally } from '../../store/useStampRally'
import { api, type StampBooth } from '../../services/api'

const categoryIcons: Record<string, string> = {
  food: '🍽️', cafe: '☕', craft: '🎨', souvenir: '🛍️', experience: '🎡', other: '🏪',
}

/**
 * 出店店舗一覧 (#1126)。データは /stamp/context の stores（TOP経由で取得済みの
 * ことが多い）。直リンク流入時は context が無いので TOP へ戻す — 表示条件判定
 * (ジオフェンス等) を二重実装しないため。
 *
 * #1886 `?event=` があればイベント詳細から取り直す。**開催前のイベントは context に
 * 入らない** (context は開催中のものしか持たない) ので、開催予定のカードから
 * この一覧を開けなかった。検索もブース番号も獲得状況も揃っているこの画面が
 * いちばん見やすいので、予定のイベントでも同じ画面を見せる。
 */
export default function StampBoothList() {
  const navigate = useNavigate()
  // #1361 戻るは履歴を1つ戻す（行き先を push すると履歴が積み上がる）
  const [params] = useSearchParams()
  const eventId = params.get('event')
  // #1898 `?event=` は企画の ID。ブースは企画に付くので、戻り先は企画詳細
  const goBack = useGoBack(eventId ? `/stamp-rally/program/${eventId}` : '/stamp-rally')
  const { t } = useI18n()
  const { context } = useStampRally()
  const [query, setQuery] = useState('')
  const [fetched, setFetched] = useState<StampBooth[] | null>(null)

  // イベントを名指しで開いたとき (開催予定など) はイベント詳細から引き当てる
  useEffect(() => {
    if (!eventId) return
    let cancelled = false
    api
      .getStampEventInfo(eventId)
      .then((res) => {
        if (cancelled) return
        // #1898 イベント詳細は**イベント全体**のブースを返す。企画の ID で名指しされたら
        // その企画のブースだけにする — カードに出した件数と一覧が食い違わないように。
        // イベント (親) の ID で来たときは全部
        const isProgram = res.programs.some((p) => p.event.id === eventId)
        setFetched(isProgram ? res.stores.filter((s) => s.eventId === eventId) : res.stores)
      })
      .catch(() => { if (!cancelled) setFetched([]) })
    return () => { cancelled = true }
  }, [eventId])

  useEffect(() => {
    if (!eventId && !context) navigate('/stamp-rally', { replace: true })
  }, [context, eventId, navigate])

  const booths = useMemo(() => {
    // #1898 context はイベントごとにまとまっているので、QR 企画の出店者を集めて並べる
    const all = eventId
      ? (fetched ?? [])
      : allPrograms(context).flatMap((p) => (p.kind === 'qr' ? p.stores : []))
    const q = query.trim().toLowerCase()
    if (!q) return all
    return all.filter(
      (b) =>
        b.name.toLowerCase().includes(q) ||
        b.kana.toLowerCase().includes(q) ||
        b.boothNo.toLowerCase().includes(q),
    )
  }, [context, eventId, fetched, query])

  // 名指しで開いたときは context の開催条件を見ない (開催前のイベントも見せる)
  if (!eventId && !context?.available) return null
  if (eventId && !fetched) return null

  return (
    <div className="min-h-screen bg-warm-white pb-10">
      <Header title={t('stampRally.storeListTitle')} onBack={goBack} />

      <div className="px-4 mt-3">
        <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2.5 shadow-card">
          <Search size={16} className="text-text-tertiary shrink-0" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('stampRally.searchPlaceholder')}
            className="flex-1 text-sm font-body outline-none bg-transparent"
          />
        </div>
      </div>

      <div className="flex flex-col gap-3 px-4 mt-4">
        {booths.length === 0 && (
          <p className="py-12 text-center text-sm font-body text-text-secondary">
            {t('stampRally.noResults')}
          </p>
        )}
        {booths.map((booth) => (
          <button
            key={booth.id}
            onClick={() =>
              navigate(
                `/stamp-rally/stores/${booth.id}${eventId ? `?event=${eventId}` : ''}`,
              )
            }
            className="flex flex-row bg-white rounded-xl shadow-card p-3 text-left tap-card animate-fade-in"
          >
            {booth.thumbnailUrl ? (
              <img src={booth.thumbnailUrl} className="w-16 h-16 rounded-xl object-cover shrink-0" alt="" />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center text-2xl shrink-0">
                {categoryIcons[booth.category] || '🏪'}
              </div>
            )}
            <div className="flex flex-col justify-center ml-3 min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <p className="font-body font-medium text-text-primary truncate">{booth.name}</p>
                {booth.links.some((l) => l.type === 'instagram') && (
                  <Instagram size={13} className="text-pink-500 shrink-0" />
                )}
                {booth.links.some((l) => l.type !== 'instagram') && (
                  <LinkIcon size={13} className="text-text-tertiary shrink-0" />
                )}
              </div>
              {booth.boothNo && (
                <p className="text-xs font-body text-text-tertiary mt-0.5">
                  {t('stampRally.boothNo', { no: booth.boothNo })}
                </p>
              )}
            </div>
            <div className="flex items-center ml-2 shrink-0">
              {booth.stamped ? (
                <span className="flex items-center gap-1 text-xs font-body font-medium text-matcha">
                  <CheckCircle2 size={16} />
                  {t('stampRally.stamped')}
                </span>
              ) : (
                <span className="w-6 h-6 rounded-full border-2 border-dashed border-gray-300" />
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}
